import { useState, useEffect, lazy, startTransition, Suspense } from "react";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  TextField,
  FormControl,
  RadioGroup,
  Select,
  Radio,
  MenuItem,
  FormControlLabel,
  InputLabel,
  Typography,
  Switch,
  Box,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";

const SquarePhotoSelector = lazy(() =>
  import("../sections/auth/signup/SquarePhotoSelector"),
);
const AddCvDialog = lazy(() =>
  import("src/sections/@dashboard/Contact/AddCvDialog"),
);
const WomanIcon = lazy(() => import("@mui/icons-material/Woman"));
const ManIcon = lazy(() => import("@mui/icons-material/Man"));
const SaveIcon = lazy(() => import("@mui/icons-material/Save"));


const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M19,13H5V11H19V13Z"/></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

export { Android12Switch };

export default function Appearance({
  Profile,
  User,
  isSaveButtonActive,
  setIsSaveButtonActive,
  isSaving,
  handlePhotoSelect,
  handleProfileChange,
  handleUserChange,
  handleSave,
  handleCoverPhotoSelect,
}) {
  const [isFormValid, setFormValid] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    startTransition(() => {
      validateForm();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Profile, User]);

  const validateForm = () => {
    const firstName = User?.firstName || "";
    const lastName = User?.lastName || "";
    const occupation = Profile?.occupation || "";

    const isFirstNameValid = /^[A-Za-z ]{3,32}$/.test(firstName);
    const isLastNameValid = /^[A-Za-z ]{3,32}$/.test(lastName);
    const isOccupationValid =
      /^[A-Za-z0-9\s\-_.,/|&()@#$%^*+=!?:;'""[\]{}~`]{3,100}$/.test(occupation);

    const keywords = (Profile?.keywords || "").split(",").filter((k) => k.trim());
    const keywordsLimit = User.category === "Business" ? 10 : 5;
    const isKeywordsValid =
      keywords.length <= keywordsLimit && (Profile?.keywords || "").length <= 100;

    startTransition(() => {
      setErrors({
        firstName:
          firstName.length > 32
            ? "First name must be between 2 and 50 characters long."
            : isFirstNameValid ? "" : "First name can only contain letters and hyphens.",
        lastName:
          lastName.length > 32
            ? "Last name must be between 2 and 50 characters long."
            : isLastNameValid ? "" : "Last name can only contain letters and hyphens.",
        occupation:
          occupation.length > 100
            ? "Occupation must be between 3 and 100 characters long."
            : occupation.length < 3 && occupation.length > 0
              ? "Occupation must be at least 3 characters long."
              : occupation.length === 0 || isOccupationValid
                ? ""
                : "Occupation can contain letters, numbers, spaces, and common special characters (/, |, -, _, ., etc.).",
        keywords:
          (Profile?.keywords || "").length > 100
            ? "Keywords total length must be less than 100 characters."
            : keywords.length > keywordsLimit
              ? `Your plan allows only ${keywordsLimit} keywords.`
              : "",
      });

      setFormValid(
        isFirstNameValid &&
        isLastNameValid &&
        (occupation.length === 0 || isOccupationValid) &&
        isKeywordsValid
      );
    });
  };

  const handleSubmit = (e) => {
    startTransition(() => {
      e.preventDefault();
      if (isFormValid) handleSave();
      else {
        toast.error("Please validate your information", {
          position: "top-center",
          autoClose: 1000,
        });
      }
    });
  };

  return (
    <Grid>
      <Suspense fallback={<CircularProgress />}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardHeader
            title="Profile Settings"
            subheader="Customize your profile information and appearance"
            sx={{
              textAlign: "center",
              "& .MuiCardHeader-title": {
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "primary.main",
              },
              "& .MuiCardHeader-subheader": {
                fontSize: "0.9rem",
                color: "text.secondary",
              },
            }}
          />
          <CardContent sx={{ padding: { xs: 2, md: 3 } }}>
            {/* Profile Header Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: "text.primary",
                  fontWeight: 500,
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                  display: "inline-block",
                }}
              >
                Profile Header
              </Typography>

              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: 200, md: 250 },
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  backgroundImage: Profile.profileCoverPicture
                    ? `url(${Profile.profileCoverPicture})`
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <SquarePhotoSelector onSelect={handleCoverPhotoSelect} />
                </Box>

                {!Profile.profileCoverPicture && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      Add Cover Photo
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Make your profile stand out
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  mt: -8,
                  px: 3,
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flex: 1 }}>
                  <Box
                    sx={{
                      position: "relative",
                      "&:hover .change-photo-btn": { opacity: 1, transform: "scale(1)" },
                    }}
                  >
                    <Avatar
                      src={Profile.profilePicture}
                      alt="Profile Picture"
                      sx={{
                        width: { xs: 120, md: 150 },
                        height: { xs: 120, md: 150 },
                        border: "4px solid white",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        transition: "all 0.3s ease",
                      }}
                    />
                    <Box
                      className="change-photo-btn"
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        width: 32,
                        height: 32,
                        backgroundColor: "#ff715b",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(255, 113, 91, 0.4)",
                        cursor: "pointer",
                        opacity: 0,
                        transform: "scale(0.8)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        border: "2px solid white",
                        "&:hover": {
                          backgroundColor: "#ff5722",
                          transform: "scale(1.1)",
                          boxShadow: "0 6px 16px rgba(255, 113, 91, 0.6)",
                        },
                      }}
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.onchange = (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (!file.type.startsWith("image/")) {
                              toast.error("Please select a valid image file", { position: "top-center", autoClose: 3000 });
                              return;
                            }
                            const maxSize = 3 * 1024 * 1024;
                            if (file.size > maxSize) {
                              toast.error("File size exceeds the 3MB limit. Please select a smaller file.", { position: "top-center", autoClose: 3000 });
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              try {
                                handlePhotoSelect(event.target.result);
                                toast.success("Profile picture updated successfully. Remember to click 'Save Profile' to apply changes.", { position: "top-center", autoClose: 1000 });
                              } catch (error) {
                                toast.error("Error processing image", { position: "top-center", autoClose: 3000 });
                              }
                            };
                            reader.onerror = () => {
                              toast.error("Error reading file", { position: "top-center", autoClose: 3000 });
                            };
                            reader.readAsDataURL(file);
                          }
                        };
                        input.click();
                      }}
                    >
                      <i className="fas fa-camera" style={{ color: "white", fontSize: "14px" }} />
                    </Box>
                  </Box>
                </Box>

                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="gender"
                    value={Profile.gender}
                    onChange={handleProfileChange}
                    sx={{ justifyContent: "center" }}
                  >
                    <FormControlLabel
                      value="male"
                      control={
                        <Radio size="small" sx={{ color: "text.secondary", "&.Mui-checked": { color: "primary.main" } }} />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ManIcon sx={{ color: Profile.gender === "male" ? "primary.main" : "text.secondary", fontSize: "1.2rem" }} />
                          <Typography variant="body2" sx={{ color: Profile.gender === "male" ? "primary.main" : "text.secondary", fontWeight: Profile.gender === "male" ? 500 : 400 }}>
                            Male
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="female"
                      control={
                        <Radio size="small" sx={{ color: "text.secondary", "&.Mui-checked": { color: "primary.main" } }} />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <WomanIcon sx={{ color: Profile.gender === "female" ? "primary.main" : "text.secondary", fontSize: "1.2rem" }} />
                          <Typography variant="body2" sx={{ color: Profile.gender === "female" ? "primary.main" : "text.secondary", fontWeight: Profile.gender === "female" ? 500 : 400 }}>
                            Female
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            {/* Personal Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, color: "text.primary", fontWeight: 500, borderBottom: "2px solid", borderColor: "primary.main", pb: 1, display: "inline-block" }}>
                Personal Information
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="firstName" label="First Name" value={User.firstName} onChange={handleUserChange} error={!!errors.firstName} helperText={errors.firstName} variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="lastName" label="Last Name" value={User.lastName} onChange={handleUserChange} error={!!errors.lastName} helperText={errors.lastName} variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="userName" label="Username" value={Profile?.userName || ""} onChange={handleProfileChange} disabled variant="outlined" helperText="Username cannot be changed" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "action.hover" } }} />
                </Grid>
              </Grid>
            </Box>

            {/* Additional Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, color: "text.primary", fontWeight: 500, borderBottom: "2px solid", borderColor: "primary.main", pb: 1, display: "inline-block" }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth variant="outlined" name="occupation" label="Occupation"
                    value={Profile?.occupation || ""}
                    onChange={handleProfileChange}
                    error={!!(errors.occupation && (Profile?.occupation || "").trim() !== "")}
                    helperText={errors.occupation}
                    InputLabelProps={{ shrink: !!Profile.occupation }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="country-label">Country/Location</InputLabel>
                    <Select
                      labelId="country-label" id="country-select" name="country"
                      value={Profile.country || ""} onChange={handleProfileChange} label="Location"
                      sx={{ borderRadius: 2 }}
                      MenuProps={{ PaperProps: { sx: { mt: 1, maxHeight: 250, borderRadius: 2 } }, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" } }}
                    >
                      <MenuItem value="Tunisia">Tunisia</MenuItem>
                      <MenuItem value="Sfax">Sfax</MenuItem>
                      <MenuItem value="Sousse">Sousse</MenuItem>
                      <MenuItem value="Kairouan">Kairouan</MenuItem>
                      <MenuItem value="Bizerte">Bizerte</MenuItem>
                      <MenuItem value="Gabes">Gabes</MenuItem>
                      <MenuItem value="Ariana">Ariana</MenuItem>
                      <MenuItem value="Gafsa">Gafsa</MenuItem>
                      <MenuItem value="Marsa">Marsa</MenuItem>
                      <MenuItem value="Tataouine">Tataouine</MenuItem>
                      <MenuItem value="Hammamet">Hammamet</MenuItem>
                      <MenuItem value="Mestir">Mestir</MenuItem>
                      <MenuItem value="Nabeul">Nabeul</MenuItem>
                      <MenuItem value="Beja">Beja</MenuItem>
                      <MenuItem value="Mahdia">Mahdia</MenuItem>
                      <MenuItem value="Ben Arous">Ben Arous</MenuItem>
                      <MenuItem value="Zarzis">Zarzis</MenuItem>
                      <MenuItem value="Medenine">Medenine</MenuItem>
                      <MenuItem value="Siliana">Siliana</MenuItem>
                      <MenuItem value="Kasserine">Kasserine</MenuItem>
                      <MenuItem value="Tozeur">Tozeur</MenuItem>
                      <MenuItem value="Kebili">Kebili</MenuItem>
                      <MenuItem value="Jandouba">Jandouba</MenuItem>
                      <MenuItem value="Zaghouan">Zaghouan</MenuItem>
                      <MenuItem value="Sbitla">Sbitla</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Keywords */}
            <Box sx={{ mb: 4 }}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "1px solid", borderColor: "divider" }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Box>
             
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Search Keywords</Typography>
                    <TextField
                      fullWidth variant="outlined" name="keywords" label="Keywords (comma separated)"
                      placeholder="e.g. Developer, Designer, React, Node.js"
                      value={Profile?.keywords || ""}
                      onChange={(e) => { handleProfileChange(e); setIsSaveButtonActive(true); }}
                      error={!!errors.keywords}
                      helperText={errors.keywords || `Adding keywords helps others find you. (${(Profile?.keywords || "").split(",").filter((k) => k.trim()).length} / ${User.category === "Business" ? 10 : 5} keywords used)`}
                      InputLabelProps={{ shrink: !!Profile.keywords }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                      {(Profile?.keywords || "").split(",").map((tag, index) => {
                        const trimmedTag = tag.trim();
                        if (!trimmedTag) return null;
                        return (
                            <Chip key={index} label={trimmedTag} size="small" color={index < (User.category === "Business" ? 10 : 5) ? "primary" : "error"} variant="filled" sx={{ borderRadius: "6px" }} />
                          );
                      })}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </CardContent>

          <LoadingButton
            color="primary"
            onClick={handleSubmit}
            variant="contained"
            loading={isSaving}
            disabled={!isSaveButtonActive || !isFormValid}
            sx={{ margin: "10px 21px 20px" }}
            endIcon={<SaveIcon />}
          >
            Save profile
          </LoadingButton>
        </Card>
      </Suspense>

      {["Student", "Freelance", "Professional", "Business", "Free"].includes(User.category) && (
        <Suspense fallback={<CircularProgress />}>
          <AddCvDialog />
        </Suspense>
      )}
    </Grid>
  );
}
